export const T12RedFlags = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-red-50 border border-red-200 rounded-full mb-6">
            <span className="text-red-600 text-sm font-semibold">Warning Signs</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            T12 Red Flags That Kill Deals
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Buyers are trained to spot these warning signs in T12 statements. Understanding them helps you avoid deal-killing issues before listing your property.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl mb-6">
              <img 
                src="https://public.readdy.ai/ai/img_res/4725c750720549f52f278b4cdfc61e08.jpg"
                alt="Investor identifying red flags in T12 financial statements"
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3">Impact of Red Flags</h3>
              <p className="text-gray-700 mb-4">
                Even one major red flag can reduce your sale price by 15-30% or kill the deal entirely. Buyers will either walk away or demand significant price reductions to compensate for perceived risk.
              </p>
              <div className="bg-white rounded-lg p-4 border border-red-200">
                <div className="text-sm text-gray-700 mb-2"><strong>Example Impact:</strong></div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Expected Value (clean T12):</span>
                    <span className="font-semibold">$2,800,000</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>With Major Red Flags:</span>
                    <span className="font-semibold">$2,100,000</span>
                  </div>
                  <div className="flex justify-between border-t border-red-300 pt-1 mt-1 font-bold text-red-700">
                    <span>Lost Value:</span>
                    <span>-$700,000 (25%)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-down-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Declining Income Trends</h3>
                  <p className="text-gray-700 mb-3">
                    Month-over-month or quarter-over-quarter decreases in rental income signal problems with tenant retention, market demand, or property competitiveness.
                  </p>
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2"><strong>What Buyers Think:</strong></p>
                    <p className="text-sm text-gray-700">"If income is declining now, it will likely continue declining after I buy. I need to factor in further income loss and adjust my offer down significantly."</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-door-open-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">High or Increasing Vacancy</h3>
                  <p className="text-gray-700 mb-3">
                    Vacancy rates above 10% or steadily increasing vacancy over the T12 period indicate serious property or management issues.
                  </p>
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2"><strong>Buyer Response:</strong></p>
                    <p className="text-sm text-gray-700">Buyers will underwrite the property at current vacancy levels or higher, dramatically reducing NOI and property value. They may also require extensive property improvements as a condition of purchase.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-tools-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unusually Low Maintenance Expenses</h3>
                  <p className="text-gray-700 mb-3">
                    Maintenance expenses below 8% of gross income suggest deferred maintenance. Buyers know they'll inherit these problems.
                  </p>
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2"><strong>Buyer Adjustment:</strong></p>
                    <p className="text-sm text-gray-700">Buyers will add "normalized" maintenance expenses to their underwriting, reducing your NOI by $20,000-50,000+ annually. They'll also budget for immediate capital expenditures and reduce their offer accordingly.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-damage-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Incomplete or Inconsistent Documentation</h3>
                  <p className="text-gray-700 mb-3">
                    Missing months, inconsistent formatting, or inability to provide supporting documentation raises immediate trust issues.
                  </p>
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2"><strong>Deal Killer:</strong></p>
                    <p className="text-sm text-gray-700">Most sophisticated buyers will walk away immediately. Those who stay will assume the worst and make lowball offers. Lenders will refuse to finance the purchase.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Complete Red Flag Checklist</h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-money-dollar-circle-line text-red-600 text-xl"></i>
                Income Red Flags
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Declining rental income</div>
                    <p className="text-sm text-gray-600">Month-over-month or year-over-year decreases</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Large gap between scheduled and collected rent</div>
                    <p className="text-sm text-gray-600">Suggests collection problems or bad tenants</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Significant one-time income items</div>
                    <p className="text-sm text-gray-600">Insurance claims, settlements inflating revenue</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Rents significantly above market</div>
                    <p className="text-sm text-gray-600">Unsustainable, tenants will leave at lease end</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">High vacancy loss (10%+)</div>
                    <p className="text-sm text-gray-600">Indicates property or market problems</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Inconsistent other income</div>
                    <p className="text-sm text-gray-600">Wild fluctuations suggest unreliable revenue</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-file-list-line text-red-600 text-xl"></i>
                Expense Red Flags
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Rapidly increasing expenses</div>
                    <p className="text-sm text-gray-600">Suggests deteriorating property condition</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Unusually low maintenance costs</div>
                    <p className="text-sm text-gray-600">Deferred maintenance will hit new owner</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Missing property management fees</div>
                    <p className="text-sm text-gray-600">Buyers will add 8-10% to expenses</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Expenses above 50% of income</div>
                    <p className="text-sm text-gray-600">Indicates serious operational problems</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Large unexplained expense items</div>
                    <p className="text-sm text-gray-600">Lack of transparency raises concerns</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Utilities far above market norms</div>
                    <p className="text-sm text-gray-600">Suggests inefficient systems or leaks</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-file-warning-line text-red-600 text-xl"></i>
              Documentation Red Flags
            </h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="font-semibold text-gray-900 mb-2">Missing Months</div>
                <p className="text-sm text-gray-700">Incomplete T12 with gaps in monthly data</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="font-semibold text-gray-900 mb-2">No Supporting Docs</div>
                <p className="text-sm text-gray-700">Can't provide bank statements or receipts</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="font-semibold text-gray-900 mb-2">Inconsistent Format</div>
                <p className="text-sm text-gray-700">Different categories or calculations each month</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="font-semibold text-gray-900 mb-2">Handwritten Changes</div>
                <p className="text-sm text-gray-700">Manual alterations without explanation</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="font-semibold text-gray-900 mb-2">Doesn't Match Tax Returns</div>
                <p className="text-sm text-gray-700">Major discrepancies with Schedule E</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="font-semibold text-gray-900 mb-2">Delayed Delivery</div>
                <p className="text-sm text-gray-700">Takes weeks to produce T12 documentation</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border border-green-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">How to Avoid Red Flags</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                    <span>Maintain accurate monthly records throughout ownership</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                    <span>Keep all supporting documentation organized and accessible</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                    <span>Address property issues before they show up in T12</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                    <span>Use consistent accounting methods and categories</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                    <span>Have your accountant review T12 before listing</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-2xl text-yellow-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">If You Have Red Flags</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1"></i>
                    <span>Be transparent - hiding issues makes them worse</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1"></i>
                    <span>Provide explanations and context for anomalies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1"></i>
                    <span>Consider waiting 6-12 months to improve T12</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1"></i>
                    <span>Target cash buyers who can look past issues</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-1"></i>
                    <span>Price property to reflect T12 reality</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};